import Vue from 'vue'
import jwt from 'jsonwebtoken'
import Vuex from 'vuex'
import { RequestService } from '@/services/request.service'
import { Transaction } from '../entities/Transaction'
import { POINTS_CURRENCY_ID, POINTS_SERVICE_CODE, MISC_ACCOUNT_ID } from '@/config/environment'

const requestService = new RequestService()

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    token: null,
    form: {},
    currencies: null,
    paymentMethods: null,
    facilities: null,
    exchangeRates: null,
    profileId: null,
    currency: null,
    accountDetails: null,
    booking: null,
    guest: null,
    guests: null,
    facility: null,
    station: null,
    message: {
      text: null,
      color: null,
      show: null,
      timeout: parseInt(process.env.VUE_APP_MESSAGE_TIMEOUT, 10)
    }
  },
  mutations: {
    setField (state, { field, value }) {
      Vue.set(state.form, field, value)
    },
    setMessage (state, message) {
      state.message.text = message.text
      state.message.color = message.color
      state.message.timeout = message.timeout
      state.message.show = true
    },
    setToken (state, token) {
      state.token = token
    },
    setFacility (state, facility) {
      state.facility = facility
    }
  },
  actions: {
    purchasePoints ({ state }) {
      return new Promise((resolve, reject) => {
        var transactions = []
        const tokenInfo = jwt.decode(state.token)
        if (state.form.transactionType === 'PAYMENT') {
          var price = state.form.paymentCurrencyId === state.facility.baseCurrencyId
            ? state.form.price
            : state.form.convertedPrice
          if (state.form.discount) {
            price = price - (price * state.form.discount / 100)
          }
          var transaction = new Transaction({
            amount: price,
            currencyId: state.facility.baseCurrencyId,
            paymentMethodId: state.form.paymentMethodId,
            accountId: state.accountDetails.account.id,
            guestId: state.form.guestId,
            concept: 'Points purchase',
            type: state.form.transactionType,
            operatorId: tokenInfo.sub,
            stationId: state.form.stationId
          })
          if (state.form.paymentCurrencyId !== state.facility.baseCurrencyId) {
            transaction.exchangeRate = state.form.paymentCurrency.exchangeRate.exchangeRate
            transaction.exchangeCurrencyId = state.form.paymentCurrencyId
          }
          transactions.push(transaction)
        }
        var fullPrice = state.form.price
        if (state.form.discount) {
          fullPrice = state.form.price - (state.form.price * state.form.discount / 100)
        }
        transactions.push(new Transaction({
          amount: -fullPrice,
          currencyId: state.facility.baseCurrencyId,
          accountId: state.accountDetails.account.id,
          guestId: state.form.guestId,
          concept: 'Points purchase',
          type: 'BOOKING',
          attributes: {
            service_code: POINTS_SERVICE_CODE
          },
          operatorId: tokenInfo.sub,
          stationId: state.form.stationId
        }))
        transactions.push(new Transaction({
          amount: state.form.pointsAmount,
          currencyId: POINTS_CURRENCY_ID,
          accountId: state.accountDetails.account.id,
          guestId: state.form.guestId,
          concept: 'Points purchase',
          type: 'PAYMENT',
          operatorId: tokenInfo.sub,
          stationId: state.form.stationId
        }))
        requestService.postTransactions(transactions).then(() => {
          resolve()
        }).catch((error) => {
          reject(error)
        })
      })
    },
    getExchangeRate ({ state }, currencyId) {
      return new Promise((resolve, reject) => {
        requestService.listExchangeRates(currencyId).then(async (exchangeRates) => {
          var currency = state.currencies.find((c) => {
            return c.id === currencyId
          })
          currency.exchangeRate = exchangeRates.find((e) => {
            return e.facilityId === state.facility.id
          })
          resolve(currency)
        }).catch((error) => {
          reject(error)
        })
      })
    },
    async purchaseDaypass ({ state }) {
      const dateNow = new Date()
      const checkIn = dateNow.toISOString()
      const checkOut = new Date(dateNow.setHours(23, 59, 59, 999)).toISOString()
      const params = {
        name: state.form.name,
        last_name: state.form.surname,
        facility_id: state.facility.id,
        account_id: MISC_ACCOUNT_ID,
        checkin: checkIn,
        checkout: checkOut,
        payment_method_id: state.form.paymentMethod,
        amount: parseFloat(state.form.amount),
        currency_id: state.form.currencyInFacility,
        exchange_currency_id: state.form.currencySelected !== undefined
          ? state.form.currencySelected.id
          : null,
        exchange_value: state.form.currencySelected !== undefined
          ? state.form.currencySelected.exchangeRate.exchangeRate
          : null,
        number_guests: parseInt(state.form.numberGuests),
        observations: state.form.observations
      }
      return new Promise((resolve, reject) => {
        requestService.lambdaDaypass(params).then(() => {
          resolve()
        }).catch((error) => reject(error))
      })
    },
    listCurrencies ({ state }) {
      return new Promise((resolve, reject) => {
        requestService.listCurrencies().then((currencies) => {
          state.currencies = currencies
          resolve(state.currencies)
        }).catch((error) => {
          reject(error)
        })
      })
    },
    listPaymentMethods ({ state }) {
      return new Promise((resolve, reject) => {
        requestService.listPaymentMethods().then((paymentMethods) => {
          state.paymentMethods = paymentMethods
          resolve(state.paymentMethods)
        }).catch((error) => {
          reject(error)
        })
      })
    },
    getAccount ({ state, dispatch }, accountId) {
      return new Promise((resolve, reject) => {
        requestService.getAccount(accountId).then(async (accountDetails) => {
          state.accountDetails = accountDetails
          if (accountDetails.account.active) {
            await dispatch('getProfile', { profileId: accountDetails.account.profileId, save: true })
          }
          resolve(state.accountDetails)
        }).catch((error) => {
          reject(error)
        })
      })
    },
    getRoom ({ state }, { roomId }) {
      return new Promise((resolve, reject) => {
        requestService.getRoom(roomId).then((room) => {
          resolve(room)
        }).catch((error) => {
          reject(error)
        })
      })
    },
    getProfile ({ state }, { profileId, save }) {
      return new Promise((resolve, reject) => {
        requestService.getProfile(profileId).then((profile) => {
          if (save) {
            state.accountDetails.account.profile = profile
          }
          resolve(profile)
        }).catch((error) => {
          reject(error)
        })
      })
    },
    getProfileCategory ({ state }, { id }) {
      return new Promise((resolve, reject) => {
        requestService.getProfileCategory(id).then((category) => {
          resolve(category)
        }).catch((error) => {
          reject(error)
        })
      })
    },
    getBookingGuests ({ state, dispatch }) {
      return new Promise((resolve, reject) => {
        requestService.getBookingGuests(state.booking.id).then((guests) => {
          state.booking.guests = guests
          var promises = []
          state.booking.guests.forEach((guest) => {
            promises.push(dispatch('getProfile', { profileId: guest.profileId }).then((profile) => {
              guest.profile = profile
            }))
          })
          Promise.all(promises).then(() => {
            resolve(state.booking.guests)
          })
        }).catch((error) => {
          reject(error)
        })
      })
    },
    listFacilities ({ state }) {
      return new Promise((resolve, reject) => {
        requestService.listFacilities().then((facilities) => {
          state.facilities = facilities
          resolve(state.facilities)
        }).catch((error) => {
          reject(error)
        })
      })
    },
    getBooking ({ state, dispatch }, bookingId) {
      return new Promise((resolve, reject) => {
        requestService.getBooking(bookingId).then(async (booking) => {
          state.booking = booking
          await dispatch('getFacility')
          await dispatch('getBookingGuests')
          resolve(booking)
        }).catch((error) => {
          reject(error)
        })
      })
    },
    getGuest ({ state, dispatch }, { guestId, getFacility }) {
      return new Promise((resolve, reject) => {
        requestService.getGuest(guestId).then(async (guest) => {
          state.guest = guest
          if (getFacility) {
            await dispatch('getBooking', guest.bookingId).then(async () => {
              await dispatch('getFacility')
            })
          }
          resolve(guest)
        }).catch((error) => {
          reject(error)
        })
      })
    },
    getStation ({ state }, { id }) {
      return new Promise((resolve, reject) => {
        requestService.getStation(id).then(async (station) => {
          state.station = station
          resolve(station)
        }).catch((error) => {
          reject(error)
        })
      })
    },
    getFacility ({ state, dispatch }) {
      return new Promise((resolve, reject) => {
        requestService.getFacility(state.booking.facilityId).then(async (facility) => {
          state.facility = facility
          state.facility.baseCurrency = state.currencies.find((c) => { return c.id === state.facility.baseCurrencyId })
          resolve(facility)
        }).catch((error) => {
          reject(error)
        })
      })
    },
    listExchangeRates ({ state }, currencyId) {
      return new Promise((resolve, reject) => {
        requestService.listExchangeRates(currencyId).then((exchangeRates) => {
          state.exchangeRates = exchangeRates
          resolve(state.exchangeRates)
        }).catch((error) => {
          reject(error)
        })
      })
    },
    getBookingGuestsForSignature ({ state, dispatch }, bookingId) {
      return new Promise((resolve, reject) => {
        requestService.getBookingGuests(bookingId).then(guests => {
          state.booking = { id: bookingId }
          const promises = []
          state.guests = []
          guests.forEach((guest) => {
            promises.push(dispatch('getProfile', { profileId: guest.profileId }).then((profile) => {
              guest.profile = profile
            }))
            if (guest.roomId) {
              promises.push(dispatch('getRoom', { roomId: guest.roomId }).then((room) => {
                guest.room = room
              }))
            }
            state.guests.push(guest)
          })
          Promise.all(promises).then(() => {
            resolve(state.guests)
          })
        }).catch((error) => {
          reject(error)
        })
      })
    },
    saveSignature ({ state }, { signedBy, allowedGuests, signatureImage, stationId }) {
      return new Promise((resolve, reject) => {
        const reqBody = {
          signer_guest_id: signedBy,
          allowed_guest_ids: allowedGuests,
          signature: signatureImage
        }
        if (stationId !== undefined) {
          reqBody.station_id = stationId
        }
        requestService.saveSignature(reqBody).then(() => {
          resolve()
        }).catch((error) => {
          reject(error)
        })
      })
    }
  }
})
